import type { ReactNode } from "react"

import { ESTADOS_CIVILES, GENEROS } from "@/componentes/personas/DatosDePersona"
import { NIVELES_ESTUDIO, type Persona } from "@/componentes/personas/persona"
import { comoFecha } from "@/lib/fechas"
import { PAISES } from "@/lib/paises"

/**
 * La ficha FIJA de una persona: lo que dice su expediente, para leerlo (03/11, pedido del
 * usuario: «al hacer clic en el empleado se muestra el formulario de entrada y resulta
 * incómodo»). Editar es otro gesto —«Editar ficha»—, y entonces sí salen los campos.
 *
 * ⚠ **Las mismas secciones y en el mismo orden que el formulario**, para que al pulsar
 * «Editar» cada dato siga donde estaba. Lo vacío se dice con un guion, no se esconde: en un
 * expediente, lo que falta es justo lo que hay que ver.
 */

const etiqueta = (pares: readonly (readonly [string, string])[], v: string | null | undefined) =>
  pares.find(([clave]) => clave === (v ?? ""))?.[1] ?? v ?? ""

function Dato({
  rotulo,
  children,
  ancho = false,
}: {
  rotulo: string
  children: ReactNode
  ancho?: boolean
}) {
  const vacio = children === null || children === undefined || children === ""
  return (
    <div className={ancho ? "sm:col-span-2" : undefined}>
      <dt className="text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
        {rotulo}
      </dt>
      <dd className="mt-0.5 text-sm break-words whitespace-pre-line">
        {vacio ? <span style={{ color: "var(--fg-muted)" }}>—</span> : children}
      </dd>
    </div>
  )
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="rotulo mb-3">{titulo}</h3>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
  )
}

function telefonoCorporativo(p: Persona): string {
  if (!p.telefono_corporativo) return ""
  const pais = PAISES.find((x) => x.iso === p.pais_telefono)
  return `${pais ? `${pais.prefijo} ` : ""}${p.telefono_corporativo}`
}

export function VistaDePersona({ persona: p }: { persona: Persona }) {
  const esEmpleado = p.tipo !== "CLIENTE"
  const estudios = p.estudios ?? []
  const experiencias = p.experiencias ?? []
  return (
    <div className="space-y-6" data-testid="vista-persona">
      <Seccion titulo="Datos personales">
        <Dato rotulo="Nombres">{p.nombres}</Dato>
        <Dato rotulo="Apellidos">{p.apellidos}</Dato>
        <Dato rotulo="Fecha de nacimiento">{comoFecha(p.fecha_nacimiento)}</Dato>
        <Dato rotulo="Género">{p.genero ? etiqueta(GENEROS, p.genero) : ""}</Dato>
        <Dato rotulo="Estado civil">
          {p.estado_civil ? etiqueta(ESTADOS_CIVILES, p.estado_civil) : ""}
        </Dato>
      </Seccion>

      <Seccion titulo="Contacto">
        <Dato rotulo="Teléfono">{p.telefono}</Dato>
        <Dato rotulo="Correo personal">{p.email_personal}</Dato>
        <Dato rotulo="Dirección" ancho>
          {p.direccion}
        </Dato>
        <Dato rotulo="Contacto de emergencia">{p.contacto_emergencia}</Dato>
        <Dato rotulo="Teléfono de emergencia">{p.telefono_emergencia}</Dato>
      </Seccion>

      <Seccion titulo="Identificación y perfil">
        <Dato rotulo="RIF">{p.rif}</Dato>
        <Dato rotulo="RIF vence">{comoFecha(p.rif_vence)}</Dato>
        <Dato rotulo="Cédula vence">{comoFecha(p.cedula_vence)}</Dato>
        <Dato rotulo="Nacionalidad">{p.nacionalidad}</Dato>
        <Dato rotulo="Profesión u oficio" ancho>
          {p.profesion}
        </Dato>
        <Dato rotulo="Idiomas, habilidades y otros datos del currículo" ancho>
          {p.resumen_curricular}
        </Dato>
      </Seccion>

      {esEmpleado && (
        <Seccion titulo="Vinculación">
          <Dato rotulo="Cargo">{p.cargo}</Dato>
          <Dato rotulo="Fecha de ingreso">{comoFecha(p.fecha_ingreso)}</Dato>
          <Dato rotulo="Teléfono corporativo">
            {telefonoCorporativo(p) || (
              <span style={{ color: "var(--fg-muted)" }}>El de la empresa</span>
            )}
          </Dato>
          <Dato rotulo="Correo corporativo">{p.email_corporativo}</Dato>
        </Seccion>
      )}

      <Seccion titulo="Acceso al sistema">
        <Dato rotulo="Cuenta enlazada" ancho>
          {p.usuario_id !== null
            ? `Entra al sistema${p.email_corporativo ? ` · ${p.email_corporativo}` : ""}`
            : "Sin cuenta — no entra al sistema"}
        </Dato>
      </Seccion>

      <section>
        <h3 className="rotulo mb-3">Estudios</h3>
        {estudios.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            —
          </p>
        ) : (
          <ul className="space-y-2 text-sm">
            {estudios.map((e, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: la lista se reemplaza entera
              <li key={i}>
                <span className="font-medium">{e.titulo}</span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {[
                    etiqueta(NIVELES_ESTUDIO, e.nivel),
                    e.institucion,
                    e.en_curso
                      ? `desde ${e.anio_inicio ?? "—"}, en curso`
                      : [e.anio_inicio, e.anio_fin].filter(Boolean).join(" – "),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="rotulo mb-3">Experiencia laboral</h3>
        {experiencias.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            —
          </p>
        ) : (
          <ul className="space-y-2 text-sm">
            {experiencias.map((x, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: la lista se reemplaza entera
              <li key={i}>
                <span className="font-medium">
                  {x.empresa}
                  {x.cargo ? ` · ${x.cargo}` : ""}
                </span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {[x.desde, x.actual ? "actual" : x.hasta].filter(Boolean).join(" – ")}
                </span>
                {x.funciones && <span className="block text-xs">{x.funciones}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Seccion titulo="Notas">
        <Dato rotulo="Notas internas" ancho>
          {p.notas}
        </Dato>
      </Seccion>
    </div>
  )
}
